import { API } from '@/core/Api';
import { getVerifiedToken } from '@/core/U';

// OpenAI 스트리밍 응답 핸들러 인터페이스
interface StreamHandler {
  onTextChunk?: (text: string) => void;
  onImageGenerated?: (images: any[]) => void;
  onImageGenerationStarted?: () => void;
  onReasoningUpdate?: (reasoning: any) => void;
  onMessageUpdate?: (message: any) => void;
  onComplete?: (finalResult: StreamResult) => void;
  onError?: (error: Error) => void;
}

// 스트리밍 결과 타입
interface StreamResult {
  text: string;
  images: any[];
  reasoning?: any[];
  messages?: any[];
  rawResponse?: any;
}

// OpenAI GPT AI 서비스
export class OpenAIAI {
  /**
   * OpenAI API 스트리밍 응답을 처리하는 공통 함수
   * @param endpoint API 엔드포인트 (상대 경로)
   * @param requestData 요청 데이터
   * @param handlers 스트리밍 이벤트 핸들러들
   * @returns 최종 스트리밍 결과
   */
  private async streamOpenAIResponse(
    endpoint: string,
    requestData: any,
    handlers: StreamHandler,
  ): Promise<StreamResult> {
    // 인증 토큰 가져오기
    const token = await getVerifiedToken();

    // 결과 수집용 변수들
    let accumulatedText = '';
    const allImages: any[] = [];
    const allReasoning: any[] = [];
    const allMessages: any[] = [];
    let rawResponse: any = null;
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_SERVER}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ ...requestData, stream: true }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API 오류: ${response.statusText} - ${errorText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Response body is null');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');

        // 마지막 불완전한 라인은 버퍼에 보관
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();

          // 이벤트 라인 건너뛰기
          if (trimmedLine.startsWith('event: ')) {
            continue;
          }

          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6).trim();

            // 종료 신호 확인
            if (data === '[DONE]') {
              const finalResult: StreamResult = {
                text: accumulatedText,
                images: allImages,
                reasoning: allReasoning,
                messages: allMessages,
                rawResponse,
              };
              handlers.onComplete?.(finalResult);
              return finalResult;
            }

            // 빈 데이터 건너뛰기
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);
              rawResponse = parsed;

              // 텍스트 콘텐츠 처리 (choices.delta.content)
              if (parsed.choices?.[0]?.delta?.content) {
                const content = parsed.choices[0].delta.content;
                accumulatedText += content;
                handlers.onTextChunk?.(content);
              }

              // 이미지 생성 결과 처리 (output 배열)
              if (parsed.output) {
                // 이미지 생성 시작 감지 (status: "in_progress")
                const imageInProgressOutputs = parsed.output.filter(
                  (item: any) =>
                    item.type === 'image_generation_call' && item.status === 'in_progress',
                );

                if (imageInProgressOutputs.length > 0) {
                  handlers.onImageGenerationStarted?.();
                }

                // 이미지 생성 완료 처리 (status: "completed")
                const imageOutputs = parsed.output.filter(
                  (item: any) =>
                    item.type === 'image_generation_call' && item.status === 'completed',
                );

                if (imageOutputs.length > 0) {
                  const processedImages = imageOutputs
                    .map((imageData: any) => ({
                      ...imageData,
                      dataUrl: imageData.result
                        ? `data:image/${imageData.output_format || 'png'};base64,${
                            imageData.result
                          }`
                        : null,
                    }))
                    .filter((img: any) => img.dataUrl);

                  // 중복 이미지 방지: 이미 존재하는 ID는 제외
                  const newImages = processedImages.filter(
                    (newImg: any) =>
                      !allImages.some((existingImg: any) => existingImg.id === newImg.id),
                  );

                  if (newImages.length > 0) {
                    console.log(
                      `🎨 Adding ${newImages.length} new images (filtered ${
                        processedImages.length - newImages.length
                      } duplicates)`,
                    );
                    allImages.push(...newImages);
                    handlers.onImageGenerated?.(newImages);
                  }
                }

                // 추론 데이터 처리
                const reasoningOutputs = parsed.output.filter(
                  (item: any) => item.type === 'reasoning',
                );
                if (reasoningOutputs.length > 0) {
                  allReasoning.push(...reasoningOutputs);
                  handlers.onReasoningUpdate?.(reasoningOutputs);
                }

                // 메시지 데이터 처리
                const messageOutputs = parsed.output.filter((item: any) => item.type === 'message');
                if (messageOutputs.length > 0) {
                  allMessages.push(...messageOutputs);
                  handlers.onMessageUpdate?.(messageOutputs);

                  // 메시지에서 텍스트 콘텐츠 추출
                  messageOutputs.forEach((msg: any) => {
                    if (msg.content && Array.isArray(msg.content)) {
                      msg.content.forEach((contentItem: any) => {
                        if (contentItem.type === 'output_text' && contentItem.text) {
                          //text 중복방지 -> TODO 보완필요
                          if (accumulatedText.length === 0) {
                            accumulatedText += contentItem.text;
                            handlers.onTextChunk?.(contentItem.text);
                          }
                        }
                      });
                    }
                  });
                }
              }

              // finish_reason 체크
              const finishReason = parsed.choices?.[0]?.finish_reason;
              if (finishReason) {
                const finalResult: StreamResult = {
                  text: accumulatedText,
                  images: allImages,
                  reasoning: allReasoning,
                  messages: allMessages,
                  rawResponse: parsed,
                };
                handlers.onComplete?.(finalResult);
                return finalResult;
              }
            } catch (error) {
              // JSON 파싱 에러가 발생해도 스트리밍 계속 진행
              if (data.length > 100) {
                console.warn('JSON parse error for large data chunk:', error);
              } else {
                console.warn('JSON parse error:', error, 'for data:', data);
              }
            }
          }
        }
      }

      reader.releaseLock();

      // 스트리밍 완료 후 최종 결과 반환
      const finalResult: StreamResult = {
        text: accumulatedText,
        images: allImages,
        reasoning: allReasoning,
        messages: allMessages,
        rawResponse,
      };
      handlers.onComplete?.(finalResult);
      return finalResult;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      handlers.onError?.(errorObj);
      throw errorObj;
    }
  }
  /**
   * GPT-5 모델로 채팅 완료 (스트리밍)
   * @param content 사용자 메시지 내용
   * @param onChunk 스트리밍 청크 콜백 (선택사항, 제공되지 않으면 내부에서 수집)
   * @param options 추가 옵션들
   * @returns GPT-5 스트리밍 응답
   */
  async generateWithGPT5(
    content: string | { type: string; text?: string; image_url?: string | { url: string } }[],
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number; // 최대 토큰 수
      temperature?: number; // 창의성 (0-2)
      model?: string; // 모델명 (선택사항)
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model || 'gpt-5-2025-08-07', // GPT-5 supports all features
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 0.7,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
    });
  }

  /**
   * o3 모델로 추론 기능 사용 (스트리밍)
   * @param content 복잡한 문제나 질문
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추론 옵션들
   * @returns 추론 과정이 포함된 스트리밍 응답
   */
  async generateWithO3Reasoning(
    content: string,
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      reasoning_effort?: 'low' | 'medium' | 'high'; // 추론 노력 수준
      reasoning_budget?: number; // 추론 예산 (토큰 수)
      model?: string; // 모델명 (선택사항)
      onReasoningUpdate?: (reasoning: any) => void; // 추론 업데이트 콜백
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model || 'o3-2025-04-16',
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 2000,
      enableReasoning: true,
      reasoningEffort: options.reasoning_effort || 'high',
      reasoningBudget: options.reasoning_budget || 20000,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onReasoningUpdate: options.onReasoningUpdate,
    });
  }

  /**
   * GPT-5 스트리밍 생성
   * @param content 사용자 메시지 내용
   * @param onChunk 스트리밍 데이터 처리 콜백
   * @param options 추가 옵션들
   */
  async generateWithGPT5Stream(
    content: string | { type: string; text?: string; image_url?: string | { url: string } }[],
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string; // 모델명 (선택사항)
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model || 'gpt-5-2025-08-07', // GPT-5 supports all features
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onError: (error) => {
        console.error('GPT-5 Stream error:', error);
        throw error;
      },
    });
  }

  /**
   * GPT-5 비전 - 이미지 분석 (스트리밍)
   * @param content 질문 내용
   * @param imageUrl 분석할 이미지 URL (HTTP URL 또는 Base64 data URL)
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns 이미지 분석 스트리밍 결과
   */
  async analyzeImage(
    content: string,
    imageUrl: string,
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      detail?: 'low' | 'high' | 'auto'; // 이미지 처리 세부 수준
      model?: string;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model || 'gpt-5-2025-08-07', // GPT-5 supports vision reliably
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: content,
              ...(options.prompt_key && { prompt_key: options.prompt_key }),
              ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
            },
            {
              type: 'image_url',
              image_url: {
                url: imageUrl,
                detail: options.detail || 'auto',
              },
            },
          ],
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 0.7,
    };

    console.log('OpenAI Vision request (streaming):', JSON.stringify(requestData, null, 2));

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onError: (error) => {
        console.error('OpenAI Vision error:', error);
        throw new Error(error.message || '이미지 분석에 실패했습니다.');
      },
    });
  }

  /**
   * GPT-5 비전 - 다중 이미지 분석 (스트리밍)
   * @param content 질문 내용
   * @param imageUrls 분석할 이미지 URL들
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns 다중 이미지 분석 스트리밍 결과
   */
  async analyzeMultipleImages(
    content: string,
    imageUrls: string[],
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      detail?: 'low' | 'high' | 'auto';
      model?: string;
      onImageGenerated?: (imageData: any[]) => void;
      onImageGenerationStarted?: () => void;
      reasoningEffort?: string;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const contentArray: any[] = [
      {
        type: 'text',
        text: content,
        ...(options.prompt_key && { prompt_key: options.prompt_key }),
        ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
      },
    ];

    // 각 이미지를 content 배열에 추가
    imageUrls.forEach((imageUrl) => {
      contentArray.push({
        type: 'image_url',
        image_url: {
          url: imageUrl,
          detail: options.detail || 'auto',
        },
      });
    });

    const requestData = {
      model: options.model || 'gpt-5-2025-08-07', // GPT-5 supports vision reliably
      messages: [
        {
          role: 'user',
          content: contentArray,
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 0.7,
      reasoningEffort: options.reasoningEffort,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onImageGenerated: options.onImageGenerated,
      onImageGenerationStarted: options.onImageGenerationStarted,
      onError: (error) => {
        throw new Error(error.message || '다중 이미지 분석에 실패했습니다.');
      },
    });
  }

  /**
   * GPT-5 비전 스트리밍 - 이미지와 함께
   * @param content 질문 내용
   * @param imageUrl 분석할 이미지 URL
   * @param onChunk 스트리밍 콜백
   * @param options 추가 옵션들
   */
  async analyzeImageWithStream(
    content: string,
    imageUrl: string,
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      detail?: 'low' | 'high' | 'auto';
      model?: string;
      onImageGenerated?: (imageData: any[]) => void;
      onImageGenerationStarted?: () => void;
      reasoningEffort?: string;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model || 'gpt-5-2025-08-07', // GPT-5 supports vision reliably
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: content,
              ...(options.prompt_key && { prompt_key: options.prompt_key }),
              ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
            },
            {
              type: 'image_url',
              image_url: {
                url: imageUrl,
                detail: options.detail || 'auto',
              },
            },
          ],
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0, // GPT-5 only supports temperature=1.0
      reasoningEffort: options.reasoningEffort,
    };

    // console.log('OpenAI Vision request:', JSON.stringify(requestData, null, 2));

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onImageGenerated: options.onImageGenerated,
      onImageGenerationStarted: options.onImageGenerationStarted,
      onError: (error) => {
        console.error('OpenAI Vision Streaming error:', error);
        throw error;
      },
    });
  }

  /**
   * GPT-5로 창작 글쓰기 (스트리밍)
   * @param content 창작 요청 내용
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns 창작 스트리밍 결과
   */
  async generateCreativeWriting(
    content: string,
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: 'gpt-5-2025-08-07',
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.9,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onError: (error) => {
        throw new Error(error.message || '창작 글쓰기에 실패했습니다.');
      },
    });
  }

  /**
   * GPT-5로 코딩 작업 (스트리밍)
   * @param content 코딩 요청 내용
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns 코드 생성 스트리밍 결과
   */
  async generateCode(
    content: string,
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: 'gpt-5-2025-08-07',
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.3,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onError: (error) => {
        throw new Error(error.message || '코드 생성에 실패했습니다.');
      },
    });
  }

  /**
   * o3로 복잡한 수학 문제 해결 (스트리밍)
   * @param content 수학 문제
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추론 옵션들
   * @returns 수학 문제 해결 스트리밍 결과
   */
  async solveMathProblem(
    content: string,
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      reasoning_effort?: 'low' | 'medium' | 'high';
      reasoning_budget?: number;
      onReasoningUpdate?: (reasoning: any) => void; // 추론 업데이트 콜백
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: 'o3-2025-04-16',
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 2000,
      enableReasoning: true,
      reasoningEffort: options.reasoning_effort || 'high',
      reasoningBudget: options.reasoning_budget || 15000,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onReasoningUpdate: options.onReasoningUpdate,
      onError: (error) => {
        throw new Error(error.message || '수학 문제 해결에 실패했습니다.');
      },
    });
  }

  /**
   * 사용 가능한 OpenAI 모델 목록 조회
   * @returns 모델 목록
   */
  async getAvailableModels() {
    const response = await API.get('/api/ai/openai/models');

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '모델 목록 조회에 실패했습니다.');
    }
  }

  /**
   * 자동 이미지 생성이 포함된 GPT 응답 생성 (스트리밍)
   * @param content 사용자 메시지 내용 (이미지 생성 요청 포함 가능)
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns GPT 스트리밍 응답 (이미지 생성 툴 호출 결과 포함 가능)
   */
  async generateWithAutoImageGeneration(
    content: string,
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      onImageGenerated?: (imageData: any[]) => void;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    // 스트리밍 버전 호출
    return this.generateWithAutoImageGenerationStream(content, onChunk || (() => {}), options);
  }

  /**
   * 자동 이미지 생성이 포함된 GPT 응답 생성 (스트리밍)
   * @param content 사용자 메시지 내용 (이미지 생성 요청 포함 가능)
   * @param onChunk 스트리밍 청크 콜백 함수
   * @param options 추가 옵션들
   */
  async generateWithAutoImageGenerationStream(
    content: string,
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      onImageGenerated?: (imageData: any[]) => void;
      onImageGenerationStarted?: () => void;
      reasoningEffort?: string; // 추론 노력 수준
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const requestData = {
      model: options.model || 'gpt-5-2025-08-07',
      messages: [
        {
          role: 'user',
          content: content,
          ...(options.prompt_key && { prompt_key: options.prompt_key }),
          ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0,
      reasoningEffort: options.reasoningEffort || 'low',
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onImageGenerated: (images) => {
        console.log('🖼️ Completed image outputs:', images);
        options.onImageGenerated?.(images);
      },
      onImageGenerationStarted: options.onImageGenerationStarted,
      onComplete: (result) => {
        console.log('✅ Streaming completed. Final results:', {
          text: result.text,
          images: result.images,
        });
      },
      onError: (error) => {
        console.error('Auto image generation streaming error:', error);
        throw new Error(`자동 이미지 생성 스트리밍 요청에 실패했습니다: ${error.message}`);
      },
    });
  }

  /**
   * 이미지 입력을 받아서 수정/변경하는 GPT 응답 생성 (스트리밍)
   * @param content 이미지 수정 요청 내용
   * @param imageUrls 수정할 이미지 URL들 (Base64 또는 HTTP URL)
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns GPT 스트리밍 응답 (이미지 생성 툴 호출 결과 포함 가능)
   */
  async modifyImageWithPrompt(
    content: string,
    imageUrls: string[],
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      detail?: 'low' | 'high' | 'auto';
      onImageGenerated?: (imageData: any[]) => void;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const contentArray: any[] = [
      {
        type: 'text',
        text: content,
        ...(options.prompt_key && { prompt_key: options.prompt_key }),
        ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
      },
    ];

    imageUrls.forEach((imgUrl) => {
      contentArray.push({
        type: 'image_url',
        image_url: {
          url: imgUrl,
          detail: options.detail || 'auto',
        },
      });
    });

    const requestData = {
      model: options.model || 'gpt-5-2025-08-07',
      messages: [
        {
          role: 'user',
          content: contentArray,
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onImageGenerated: options.onImageGenerated,
      onError: (error) => {
        throw new Error(error.message || '이미지 수정 요청에 실패했습니다.');
      },
    });
  }

  /**
   * 여러 이미지를 합성하여 새로운 이미지 생성 (스트리밍)
   * @param content 이미지 합성 요청 내용
   * @param imageUrls 합성할 이미지 URL들 (Base64 또는 HTTP URL)
   * @param onChunk 스트리밍 청크 콜백 (선택사항)
   * @param options 추가 옵션들
   * @returns GPT 스트리밍 응답 (이미지 생성 툴 호출 결과 포함 가능)
   */
  async combineImagesWithPrompt(
    content: string,
    imageUrls: string[],
    onChunk?: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      detail?: 'low' | 'high' | 'auto';
      onImageGenerated?: (imageData: any[]) => void;
      prompt_key?: string;
      prompt_variable?: Record<string, any>;
    } = {},
  ): Promise<StreamResult> {
    const contentArray: any[] = [
      {
        type: 'text',
        text: content,
        ...(options.prompt_key && { prompt_key: options.prompt_key }),
        ...(options.prompt_variable && { prompt_variable: options.prompt_variable }),
      },
    ];

    // 각 이미지를 content 배열에 추가
    imageUrls.forEach((imageUrl) => {
      contentArray.push({
        type: 'image_url',
        image_url: {
          url: imageUrl,
          detail: options.detail || 'auto',
        },
      });
    });

    const requestData = {
      model: options.model || 'gpt-5-2025-08-07',
      messages: [
        {
          role: 'user',
          content: contentArray,
        },
      ],
      max_tokens: options.max_tokens || 4000,
      temperature: options.temperature || 1.0,
    };

    return this.streamOpenAIResponse('/api/ai/openai/chat/completions', requestData, {
      onTextChunk: onChunk,
      onImageGenerated: options.onImageGenerated,
      onError: (error) => {
        throw new Error(error.message || '이미지 합성 요청에 실패했습니다.');
      },
    });
  }

  /**
   * OpenAI 테스트용 예제 프롬프트들
   */
  getExamplePrompts() {
    return {
      gpt5: [
        '양자 컴퓨팅을 간단한 용어로 설명해주세요. 100자 이내로.',
        'AI와 미래 사회에 대한 짧은 에세이를 작성해주세요.',
        '효율적인 학습 방법 5가지를 알려주세요.',
        '곰돌이랑 벌이 누워있는 모습 그려줘',
      ],
      auto_image: [
        '발바닥에 발가락을 표현해줘. 귀여운 만화 스타일로 그려줘.',
        '집 앞마당에 있는 작은 연못을 그림으로 그려줘. 연못에는 연꽃이 피어있고 개구리가 있어야 해.',
        '우주에서 지구를 바라보는 우주비행사의 모습을 그려줘.',
        '카페에서 커피를 마시는 고양이 그림을 그려줘.',
        '무지개가 걸린 하늘 아래 초원에 있는 나무 한 그루를 그려줘.',
      ],
      image_modify: [
        '이 이미지에 무지개를 추가해서 다시 그려줘.',
        '이 이미지의 배경을 밤하늘로 바꿔서 새로운 그림을 만들어줘.',
        '이 이미지에 있는 물체들을 만화 스타일로 변경해서 그려줘.',
        '이 이미지의 색상을 따뜻한 톤으로 바꿔서 다시 그려줘.',
        '이 이미지에 꽃과 나비를 추가해서 더 아름답게 만들어줘.',
      ],
      image_combine: [
        '이 여러 이미지들을 하나로 합성해서 새로운 그림을 만들어줘.',
        '이 이미지들의 요소들을 조합해서 판타지 풍경을 그려줘.',
        '이 이미지들에 있는 캐릭터들을 한 장면에 모아서 그려줘.',
        '이 이미지들의 스타일을 섞어서 새로운 아트워크를 만들어줘.',
        '이 이미지들을 참고해서 하나의 완성된 일러스트를 그려줘.',
      ],
      o3_reasoning: [
        '뉴욕에서 2시에 출발한 기차가 시속 80마일로 달리고, 시카고에서 3시에 출발한 기차가 시속 90마일로 뉴욕 방향으로 달립니다. 두 도시 간 거리가 790마일일 때, 언제 만날까요? 단계별로 계산해주세요.',
        '직사각형 정원의 길이가 폭보다 30% 더 깁니다. 둘레가 182피트일 때 가로세로 크기는? 과정을 보여주세요.',
        '두 개의 주사위를 던져서 나온 수의 합이 7이 될 확률과 곱이 12가 될 확률을 비교해주세요.',
      ],
      creative: [
        '인공지능과 미래에 대한 하이쿠를 작성해주세요.',
        '로봇이 감정을 발견하는 짧은 이야기를 써주세요.',
        '우주 여행을 주제로 한 시를 작성해주세요.',
      ],
      coding: [
        '소수를 확인하는 Python 함수를 주석과 함께 작성해주세요.',
        '이진 탐색 알고리즘을 JavaScript로 구현해주세요.',
        'React 컴포넌트로 간단한 투두 리스트를 만들어주세요.',
      ],
      math: [
        '한 변의 길이가 a인 정육면체의 부피와 표면적을 구하는 공식을 유도해주세요.',
        '피보나치 수열의 일반항 공식을 설명해주세요.',
        '삼각형의 넓이를 구하는 다양한 방법들을 설명해주세요.',
      ],
      vision: [
        '이 이미지에서 무엇을 볼 수 있는지 자세히 설명해주세요.',
        '이미지에 있는 사람들의 감정이나 표정을 분석해주세요.',
        '이미지의 색상, 구도, 스타일적 특징을 설명해주세요.',
      ],
      vision_multi: [
        '이 두 이미지를 비교하고 공통점과 차이점을 설명해주세요.',
        '여러 이미지에서 일관된 패턴이나 주제를 찾아보세요.',
        '이미지들 간의 시간적 순서나 연관성이 있다면 설명해주세요.',
      ],
    };
  }

  /**
   * OpenAI 모델별 특징 설명
   */
  getModelDescriptions() {
    return {
      'gpt-5-2025-08-07': '최신 GPT-5 모델 - 향상된 추론 능력과 창의성을 제공',
      'o3-2025-04-16': '추론 특화 o3 모델 - 복잡한 문제 해결과 수학적 추론에 최적화',
    };
  }

  /**
   * 추론 노력 수준별 설명
   */
  getReasoningEffortLevels() {
    return {
      low: '빠른 응답 - 기본적인 추론',
      medium: '균형잡힌 추론 - 적당한 깊이',
      high: '깊은 추론 - 복잡한 문제 해결에 최적',
    };
  }
}
